/**
 * Housekeeping HK-1 main workspace.
 * Layout: Top KPI tiles + tabbed columns (Task Board · Room Board · Priority Rooms · Inspections)
 * Attendants view uses /housekeeping/me route below.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { ClipboardList, Users, AlertTriangle, CheckCircle2, RefreshCw, PlayCircle, PauseCircle, CheckCheck, XCircle, UserPlus, Sparkles, Clock, MapPin, Search, Filter } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import RoomReadinessIndicator from "@/components/room/RoomReadinessIndicator";
import { useHotelContext } from "@/contexts/HotelContext";

const PRIORITY_TONE = { LOW: "neutral", NORMAL: "info", HIGH: "warning", URGENT: "error", VIP: "success" };
const STATE_LABEL = {
  pending: "Pending", assigned: "Assigned", accepted: "Accepted",
  in_progress: "Cleaning", paused: "Paused",
  pending_inspection: "Waiting Inspection", completed: "Completed", cancelled: "Cancelled",
};
const STATE_TONE = {
  pending: "neutral", assigned: "info", accepted: "info",
  in_progress: "warning", paused: "warning",
  pending_inspection: "warning", completed: "success", cancelled: "neutral",
};

export default function Housekeeping() {
  // Active hotel from the platform-wide HotelContext (sidebar switcher).
  const { ctx } = useHotelContext() || {};
  const hotelId = ctx?.hotel_id;
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [roomBoard, setRoomBoard] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [tab, setTab] = useState("tasks");
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [taskDrawerId, setTaskDrawerId] = useState(null);
  const [inspDrawerId, setInspDrawerId] = useState(null);

  const refresh = useCallback(async () => {
    if (!hotelId) return;
    // HK-DIRTY-2 · Use allSettled so one failing endpoint (e.g. /inspections
    // returning 400 when no perms) doesn't blank out the Task Board + Room
    // Board + Dashboard tiles for the whole page. Each panel renders whatever
    // data it got; failed panels toast an error but the rest stay populated.
    const [dRes, tRes, rRes, iRes] = await Promise.allSettled([
      api.get(`/housekeeping/dashboard?hotel_id=${hotelId}`),
      api.get(`/housekeeping/tasks?hotel_id=${hotelId}&limit=200`),
      api.get(`/housekeeping/room-board?hotel_id=${hotelId}`),
      api.get(`/housekeeping/inspections`),
    ]);
    if (dRes.status === "fulfilled") setDashboard(dRes.value.data);
    if (tRes.status === "fulfilled") setTasks(tRes.value.data || []);
    if (rRes.status === "fulfilled") setRoomBoard(rRes.value.data || []);
    if (iRes.status === "fulfilled") setInspections(iRes.value.data || []);
    // Surface only the first failure so the toast bar isn't spammed
    const firstFail = [dRes, tRes, rRes, iRes].find(x => x.status === "rejected");
    if (firstFail) {
      const err = firstFail.reason;
      // eslint-disable-next-line no-console
      console.warn("[housekeeping] partial load failure:", err?.response?.data || err?.message);
    }
  }, [hotelId]);

  useEffect(() => { refresh(); }, [refresh]);

  const filteredTasks = useMemo(() => {
    let out = tasks;
    if (stateFilter !== "all") out = out.filter(t => t.state === stateFilter);
    if (q) {
      const s = q.toLowerCase();
      out = out.filter(t =>
        (t.room_number || "").toString().toLowerCase().includes(s) ||
        (t.task_type || "").toLowerCase().includes(s) ||
        (t.notes || "").toLowerCase().includes(s));
    }
    return out;
  }, [tasks, stateFilter, q]);

  const t = dashboard?.tiles || {};
  const attendantsAvail = t.attendants_available ?? 0;
  const attendantsTotal = t.attendants_total ?? 0;

  return (
    <div className="space-y-6" data-testid="housekeeping-page">
      <PageHeader title="Housekeeping" subtitle="Operational task management, inspections, and room readiness.">
        <div className="flex gap-2 items-center">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="hk-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
          </Button>
        </div>
      </PageHeader>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hk-tiles">
        <Tile label="Ready" dot="#14C48E"       value={t.ready}              testid="tile-ready" />
        <Tile label="Dirty" dot="#DC2626"       value={t.dirty}              testid="tile-dirty" />
        <Tile label="In Progress" dot="#F59E0B" value={t.in_progress}        testid="tile-in-progress" />
        <Tile label="Awaiting Inspection" dot="#FB923C" value={t.pending_inspection} testid="tile-pending-inspection" />
        <Tile label="Overdue" icon={AlertTriangle}      value={t.overdue}            testid="tile-overdue" tone="error" />
        <Tile label="Failed Today" icon={XCircle} value={t.failed_today}       testid="tile-failed" tone="error" />
        <Tile label="Attendants" icon={Users} value={`${attendantsAvail}/${attendantsTotal}`} testid="tile-attendants" />
      </div>

      {/* Priority Rooms row */}
      {(dashboard?.priority_rooms || []).length > 0 && (
        <Card padded testid="priority-rooms">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-hocp-textSecondary"/>
            <h3 className="font-display font-semibold text-[15px]">Priority — Arrivals today/tomorrow</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {dashboard.priority_rooms.map(r => (
              <span key={r.room_id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-hocp-border bg-hocp-alice/40"
                    data-testid={`priority-room-${r.room_id}`}>
                <b className="text-[13px]">Room {r.room_number}</b>
                <RoomReadinessIndicator status={r.current_status} size="sm"/>
                <span className="text-[11px] text-hocp-textTertiary">arrives {r.next_arrival_date}</span>
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="inline-flex bg-hocp-alice/50 rounded-lg p-0.5 border border-hocp-border">
        {[
          { id: "tasks", label: "Task Board", icon: ClipboardList },
          { id: "rooms", label: "Room Board", icon: MapPin },
          { id: "attendants", label: "Attendants", icon: Users },
          { id: "inspections", label: "Inspections", icon: CheckCircle2 },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`hk-tab-${id}`}
                  className={`px-3 py-1.5 rounded-md text-[13px] font-medium flex items-center gap-1.5
                    ${tab === id ? "bg-white shadow-sm text-hocp-textPrimary" : "text-hocp-textSecondary"}`}>
            <Icon className="w-3.5 h-3.5"/> {label}
          </button>
        ))}
      </div>

      {tab === "tasks" && (
        <>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-hocp-textTertiary"/>
              <Input placeholder="Search room, task type, notes..." aria-label="Search housekeeping tasks"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     className="pl-9" data-testid="task-search"/>
            </div>
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
                    className="rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="task-state-filter">
              <option value="all">All states</option>
              {Object.entries(STATE_LABEL).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
            </select>
          </div>
          <TaskTable tasks={filteredTasks} onOpen={setTaskDrawerId}/>
        </>
      )}

      {tab === "rooms" && <RoomBoard rooms={roomBoard}/>}
      {tab === "attendants" && <AttendantsPanel attendants={dashboard?.by_attendant || []}/>}
      {tab === "inspections" &&
        <InspectionsList inspections={inspections} onOpen={setInspDrawerId}/>}

      {/* Drawers */}
      <TaskDrawer taskId={taskDrawerId} onClose={() => setTaskDrawerId(null)} onChanged={refresh}/>
      <InspectionDrawer inspectionId={inspDrawerId} onClose={() => setInspDrawerId(null)} onChanged={refresh}/>
    </div>
  );
}

function Tile({ label, value, tone, testid, icon: Icon, dot }) {
  const toneCls = tone === "error" ? "text-hocp-error" : "text-hocp-textPrimary";
  return (
    <div className="hocp-card p-4 flex flex-col" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary flex items-center gap-1.5">
        {dot && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dot }} aria-hidden />}
        {Icon && <Icon size={14} strokeWidth={1.5} className="shrink-0" />}
        {label}
      </div>
      <div className={`font-display font-bold text-3xl mt-1 tabular-nums ${toneCls}`}>{value ?? "—"}</div>
    </div>
  );
}

function TaskTable({ tasks, onOpen }) {
  if (!tasks.length) return <p className="text-hocp-textTertiary italic text-sm px-1">No tasks match.</p>;
  return (
    <div className="hocp-card overflow-x-auto" data-testid="task-table">
      <table className="w-full text-[13px]">
        <thead className="bg-hocp-alice/40">
          <tr>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Room</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Task</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Priority</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">State</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Assigned To</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">SLA</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map(t => (
            <tr key={t.id} onClick={() => onOpen(t.id)}
                className="border-t border-hocp-border hover:bg-hocp-alice/30 cursor-pointer"
                data-testid={`task-row-${t.id}`}>
              <td className="px-3 py-2 font-mono">{t.room_number || "—"}</td>
              <td className="px-3 py-2 capitalize">{t.task_type.replace(/_/g, " ")}</td>
              <td className="px-3 py-2"><Badge tone={PRIORITY_TONE[t.priority_code] || "neutral"} size="sm">{t.priority_code}</Badge></td>
              <td className="px-3 py-2"><Badge tone={STATE_TONE[t.state]} size="sm">{STATE_LABEL[t.state]}</Badge></td>
              <td className="px-3 py-2 text-hocp-textSecondary" data-testid={`hk-task-assignee-${t.id}`}>{t.assigned_to_name || (t.assigned_to ? t.assigned_to.slice(-6) : "—")}</td>
              <td className="px-3 py-2 tabular-nums text-[11.5px]">
                <SlaChip sla={t.sla_target_at} state={t.state}/>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SlaChip({ sla, state }) {
  if (!sla || ["completed", "cancelled"].includes(state)) return <span className="text-hocp-textTertiary">—</span>;
  const target = new Date(sla).getTime();
  const now = Date.now();
  const diffMin = Math.round((target - now) / 60000);
  const overdue = diffMin < 0;
  const label = overdue ? `${Math.abs(diffMin)}m late` : `${diffMin}m left`;
  return <span className={overdue ? "text-hocp-error font-medium" : "text-hocp-textSecondary"}>{label}</span>;
}

function RoomBoard({ rooms }) {
  if (!rooms.length) return <p className="text-hocp-textTertiary italic text-sm px-1">No rooms configured. Add rooms via Master Data.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="room-board">
      {rooms.map(r => (
        <div key={r.room_id} className="hocp-card p-3 flex items-center justify-between gap-3 md:flex-col md:text-center md:p-2.5 md:gap-1" data-testid={`room-tile-${r.room_id}`}>
          <div className="font-mono font-semibold text-[15px]">{r.room_number}</div>
          <RoomReadinessIndicator status={r.current_status} size="sm"/>
          {r.assigned_to_name && (
            <div className="text-[10.5px] text-hocp-textTertiary truncate max-w-[40%] md:max-w-full">{r.assigned_to_name}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function AttendantsPanel({ attendants }) {
  if (!attendants.length) return <p className="text-hocp-textTertiary italic text-sm px-1">No attendants found.</p>;
  return (
    <div className="hocp-card overflow-x-auto" data-testid="attendants-panel">
      <table className="w-full text-[13px]">
        <thead className="bg-hocp-alice/40">
          <tr>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Attendant</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Status</th>
            <th className="px-3 py-2 text-center text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Open</th>
            <th className="px-3 py-2 text-center text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Done Today</th>
          </tr>
        </thead>
        <tbody>
          {attendants.map(a => (
            <tr key={a.attendant_id} className="border-t border-hocp-border" data-testid={`att-row-${a.attendant_id}`}>
              <td className="px-3 py-2 font-medium">{a.name}</td>
              <td className="px-3 py-2 capitalize">{a.availability_status.replace(/_/g, " ")}</td>
              <td className="px-3 py-2 text-center tabular-nums">{a.tasks_open}</td>
              <td className="px-3 py-2 text-center tabular-nums">{a.tasks_done_today}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InspectionsList({ inspections, onOpen }) {
  const pending = inspections.filter(i => i.outcome === "pending");
  const rest = inspections.filter(i => i.outcome !== "pending");
  return (
    <div className="space-y-4" data-testid="inspections-list">
      {pending.length > 0 && (
        <Card padded>
          <h3 className="font-display font-semibold text-[15px] mb-2">Pending inspections · {pending.length}</h3>
          <div className="space-y-2">
            {pending.map(i => (
              <div key={i.id} onClick={() => onOpen(i.id)}
                   className="p-3 rounded-lg border border-hocp-border hover:bg-hocp-alice/30 cursor-pointer flex justify-between items-center"
                   data-testid={`inspection-row-${i.id}`}>
                <div>
                  <div className="text-[13.5px] font-medium">Task {i.task_id?.slice(-6)}</div>
                  <div className="text-[11.5px] text-hocp-textSecondary">Awaiting supervisor · {i.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
                <Badge tone="warning" size="sm">Pending</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
      {rest.length > 0 && (
        <Card padded>
          <h3 className="font-display font-semibold text-[15px] mb-2">Recent outcomes</h3>
          <div className="space-y-2">
            {rest.slice(0, 20).map(i => (
              <div key={i.id} className="p-3 rounded-lg border border-hocp-border flex justify-between items-center">
                <div>
                  <div className="text-[13.5px]">Task {i.task_id?.slice(-6)}</div>
                  <div className="text-[11.5px] text-hocp-textSecondary">{i.outcome_at?.slice(0, 16).replace("T", " ")}
                    {i.failure_reason && ` — ${i.failure_reason}`}</div>
                </div>
                <Badge tone={i.outcome === "passed" ? "success" : "error"} size="sm">{i.outcome}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
      {inspections.length === 0 &&
        <p className="text-hocp-textTertiary italic text-sm px-1">No inspections yet.</p>}
    </div>
  );
}

/* ── Task Drawer ─────────────────────────────────────────────────── */
function TaskDrawer({ taskId, onClose, onChanged }) {
  const [task, setTask] = useState(null);
  const [history, setHistory] = useState([]);
  const [recs, setRecs] = useState(null);
  const load = useCallback(async () => {
    if (!taskId) return;
    const [t, h] = await Promise.all([
      api.get(`/housekeeping/tasks/${taskId}`),
      api.get(`/housekeeping/tasks/${taskId}/history`),
    ]);
    setTask(t.data); setHistory(h.data);
  }, [taskId]);
  useEffect(() => { load(); }, [load]);
  if (!taskId) return null;

  const action = async (fn, payload = {}) => {
    try {
      await api[payload.method || "post"](`/housekeeping/tasks/${taskId}/${fn}`, payload.body);
      toast.success(`Task ${fn}`); await load(); await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const loadRecs = async () => {
    try {
      const { data } = await api.post(`/housekeeping/tasks/${taskId}/recommendations`);
      setRecs(data);
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const assign = async (attendant_id) => {
    try {
      await api.post(`/housekeeping/tasks/${taskId}/assign`, { attendant_id });
      toast.success("Assigned"); setRecs(null); await load(); await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  const complete = async () => {
    try {
      await api.post(`/housekeeping/tasks/${taskId}/complete`, {});
      toast.success("Task completed"); await load(); await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="task-drawer">
        {task && (
          <div className="p-1 space-y-4">
            <div>
              <h2 className="font-display font-semibold text-xl">Room {task.room_number} · {task.task_type.replace(/_/g, " ")}</h2>
              <div className="flex gap-2 items-center mt-2">
                <Badge tone={PRIORITY_TONE[task.priority_code]}>{task.priority_code}</Badge>
                <Badge tone={STATE_TONE[task.state]}>{STATE_LABEL[task.state]}</Badge>
                {task.require_inspection && <Badge tone="warning" size="sm">Inspection required</Badge>}
                <SlaChip sla={task.sla_target_at} state={task.state}/>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {task.state === "pending" || task.state === "assigned" ?
                <Button size="sm" onClick={loadRecs} data-testid="drawer-recommend">
                  <UserPlus className="w-3.5 h-3.5 mr-1"/> Recommend attendants
                </Button> : null}
              {["assigned", "accepted", "paused"].includes(task.state) &&
                <Button size="sm" onClick={() => action("start")} data-testid="drawer-start">
                  <PlayCircle className="w-3.5 h-3.5 mr-1"/> Start
                </Button>}
              {task.state === "in_progress" &&
                <Button size="sm" variant="outline"
                        onClick={() => action("pause", { body: { reason: window.prompt("Reason?") || "unspecified" } })}
                        data-testid="drawer-pause">
                  <PauseCircle className="w-3.5 h-3.5 mr-1"/> Pause
                </Button>}
              {["in_progress", "paused"].includes(task.state) &&
                <Button size="sm" onClick={complete} data-testid="drawer-complete">
                  <CheckCheck className="w-3.5 h-3.5 mr-1"/> Complete
                </Button>}
              {["pending", "assigned", "accepted"].includes(task.state) &&
                <Button size="sm" variant="outline"
                        onClick={() => action("cancel", { body: { reason: window.prompt("Cancel reason?") || "unspecified" } })}
                        data-testid="drawer-cancel">
                  <XCircle className="w-3.5 h-3.5 mr-1"/> Cancel
                </Button>}
            </div>

            {recs && (
              <Card padded testid="drawer-recs">
                <h3 className="font-semibold text-[14px] mb-2">Recommended attendants</h3>
                {recs.candidates.length === 0 ?
                  <p className="text-sm text-hocp-textTertiary italic">{recs.note || "No candidates matched."}</p> :
                  <div className="space-y-2">
                    {recs.candidates.slice(0, 5).map(c => (
                      <div key={c.attendant_id} className="flex justify-between items-center p-2.5 rounded border border-hocp-border"
                           data-testid={`rec-${c.attendant_id}`}>
                        <div>
                          <div className="font-medium text-[13px]">{c.name}</div>
                          <div className="text-[11px] text-hocp-textTertiary">
                            Score {c.score} · {c.reasons.join(" · ")}
                          </div>
                        </div>
                        <Button size="sm" onClick={() => assign(c.attendant_id)} data-testid={`rec-assign-${c.attendant_id}`}>Assign</Button>
                      </div>
                    ))}
                  </div>}
              </Card>
            )}

            <Card padded>
              <h3 className="font-semibold text-[14px] mb-2">Timing</h3>
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div><b>SLA target:</b> {task.sla_target_minutes} min</div>
                <div><b>Started:</b> {task.started_at ? task.started_at.slice(0, 19).replace("T", " ") : "—"}</div>
                <div><b>Completed:</b> {task.completed_at ? task.completed_at.slice(0, 19).replace("T", " ") : "—"}</div>
                <div><b>Actual:</b> {task.actual_duration_minutes ? `${task.actual_duration_minutes} min` : "—"}</div>
              </div>
            </Card>

            <Card padded>
              <h3 className="font-semibold text-[14px] mb-2">History</h3>
              <ol className="space-y-1.5 text-[12px]">
                {history.map((h, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    <Clock className="w-3 h-3 mt-1 text-hocp-textTertiary"/>
                    <div>
                      <b>{h.event === "state_change" ? `${h.from_state || "—"} → ${h.to_state}` :
                            h.event === "assignment_change" ? `Assigned → ${h.to_attendant_id?.slice(-6)}` :
                            h.event === "priority_change" ? `Priority → ${h.metadata?.to_priority}` : h.event}</b>
                      <span className="text-hocp-textTertiary ml-2">{h.at?.slice(11, 19)}</span>
                      {h.reason && <div className="text-hocp-textTertiary">{h.reason}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ── Inspection Drawer ───────────────────────────────────────────── */
function InspectionDrawer({ inspectionId, onClose, onChanged }) {
  const [insp, setInsp] = useState(null);
  const [results, setResults] = useState({});
  const [failReason, setFailReason] = useState("");
  useEffect(() => {
    if (!inspectionId) return;
    (async () => {
      const { data } = await api.get(`/housekeeping/inspections?task_id=`);
      const found = data.find(x => x.id === inspectionId);
      if (found) {
        setInsp(found);
        const init = {};
        (found.checklist_snapshot || []).forEach(i => (init[i.item_code] = true));
        setResults(init);
      }
    })();
  }, [inspectionId]);
  if (!inspectionId) return null;

  const submit = async (outcome) => {
    if (outcome === "failed" && !failReason.trim()) {
      toast.error("Failure reason required"); return;
    }
    try {
      const body = {
        outcome,
        failure_reason: outcome === "failed" ? failReason : null,
        results: (insp.checklist_snapshot || []).map(i => ({
          item_code: i.item_code,
          passed: results[i.item_code] !== false,
          note: null,
        })),
      };
      await api.post(`/housekeeping/inspections/${inspectionId}/complete`, body);
      toast.success(`Inspection ${outcome}`); onClose(); await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="inspection-drawer">
        {insp && (
          <div className="p-1 space-y-4">
            <h2 className="font-display font-semibold text-xl">Inspection · Task {insp.task_id.slice(-6)}</h2>
            <div className="space-y-2">
              {(insp.checklist_snapshot || []).map(item => (
                <label key={item.item_code}
                       className="flex items-center gap-2 p-2 rounded border border-hocp-border cursor-pointer"
                       data-testid={`insp-item-${item.item_code}`}>
                  <input type="checkbox"
                         checked={results[item.item_code] !== false}
                         onChange={(e) => setResults(r => ({ ...r, [item.item_code]: e.target.checked }))}/>
                  <span className="text-[13px]">{item.label}
                    {item.mandatory && <span className="text-hocp-error ml-1">*</span>}</span>
                </label>
              ))}
            </div>
            <div>
              <label className="text-[11px] text-hocp-textTertiary">Failure reason (required if failing)</label>
              <Input value={failReason} onChange={(e) => setFailReason(e.target.value)}
                     placeholder="Bathroom not sanitised properly, bed linen dirty..."
                     data-testid="insp-fail-reason"/>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => submit("failed")} data-testid="insp-fail">
                <XCircle className="w-3.5 h-3.5 mr-1"/> Fail
              </Button>
              <Button size="sm" onClick={() => submit("passed")} data-testid="insp-pass">
                <CheckCheck className="w-3.5 h-3.5 mr-1"/> Pass
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
